import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Switch, FormControlLabel, Box, Button, Tooltip, IconButton,
  Typography, 
  Paper, Chip, Divider, Grid, 
  Card, CardContent, CardHeader, Radio, RadioGroup, FormLabel, FormControl
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useWebSocket } from '../WebSocketContext';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { isActionEnabled, buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';
import { buildForceRunPayload } from '../../Utils/forceRunUtils';

const ActionsCreateDocx = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [targetPath, setTargetPath] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [useCustomContent, setUseCustomContent] = useState(true);
  const [useGenContent, setUseGenContent] = useState(false);
  const [contentType, setContentType] = useState('custom'); // 'custom', 'generated'
  const initialSettings = useRef({});
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  const [browsingDisabled, setBrowsingDisabled] = useState(false);
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunResult, setForceRunResult] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [customContent, setCustomContent] = useState('');
  const [genContent, setGenContent] = useState('');
  const [anchorEl, setAnchorEl] = useState(null); 
  const [menuType, setMenuType] = useState(''); 
  const [conditions, setConditions] = useState([]);
  const [id, setId] = useState(settingId || 0);

  const targetPathRef = useRef(null);
  const customContentRef = useRef(null);
  const genContentRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    ws,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        let folderPathClean = folderPath.replace(/[\\/]+$/, "");
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        folderPathClean = folderPathClean + separator;
        const newText = folderPathClean + "{filename}.docx";
        setTargetPath(newText);
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    }
  });

  const customDataTags = [{ 
    key: 'tag_fileContentToWrite', 
    label: 'Data Target File Content (if exist)', 
    value: 'fileContentToWrite', 
    description: 'Content of the target file if it already exists' 
  }];

  const handleBrowseClick = () => {
    setBrowsingDisabled(true);
    handleBrowseDirectory();
  };

  // Initialize component with default settings if no settingId provided
  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');
    
    if (settingId && !isTemporaryId) {
      // Request existing settings from server for real IDs
      sendMessage(buildGetExportSettingsPayload(settingId, caseid));
    } else if (isTemporaryId) {
      // Handle temporary IDs (new workflows)
      setId(settingId);
      setIsEnabled(false);
      setTargetPath('');
      setContentType('custom');
      setUseCustomContent(true);
      setUseGenContent(false);
      setCustomContent('');
      setGenContent('');
      setConditions([]);
      
      initialSettings.current = {
        id: settingId,
        enable: false,
        target: '',
        contentType: 'custom',
        useCustomContent: true,
        useGenContent: false,
        customContent: '',
        genContent: '',
        useConditions: true,
        conditions: [],
        data: { name: 'New DOCX Flow' }
      };
      setIsSaveDisabled(false);
    }
  }, [caseid, settingId, sendMessage]);

  // Handle WebSocket messages for loading existing settings
  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        if (parsedMessage.command === 'getExportSettings') {
          if (parsedMessage.data.type === 'createdocx') {
            const { id: newId, enable, customData } = parsedMessage.data;
            setId(newId);
            setIsEnabled(isActionEnabled(enable));
            
            if (customData.target !== undefined) {
              setTargetPath(customData.target);
            }
            if (customData?.contentType !== undefined) {
              setContentType(customData.contentType);
              // Sync the boolean flags with contentType
              setUseCustomContent(customData.contentType === 'custom');
              setUseGenContent(customData.contentType === 'generated');
            } else {
              // Default values if no contentType specified
              setContentType('custom');
              setUseCustomContent(true);
              setUseGenContent(false);
            }
            
            // Only override with explicit values if they exist
            if (customData?.useCustomContent !== undefined) setUseCustomContent(customData.useCustomContent);
            if (customData?.useGenContent !== undefined) setUseGenContent(customData.useGenContent);
            
            if (customData?.customContent !== undefined) {
              setCustomContent(customData.customContent);
            }
            
            if (customData?.genContent !== undefined) {
              setGenContent(customData.genContent);
            }
            
            if (customData?.conditions !== undefined) setConditions(customData.conditions);

            // Store initial settings for comparison
            const finalContentType = customData?.contentType || 'custom';
            initialSettings.current = {
              id: newId,
              enable: isActionEnabled(enable),
              target: customData.target || '',
              contentType: finalContentType,
              useCustomContent: finalContentType === 'custom',
              useGenContent: finalContentType === 'generated',
              customContent: customData?.customContent || '',
              genContent: customData?.genContent || '',
              useConditions: true,
              conditions: customData?.conditions || []
            };
            setIsSaveDisabled(true);
            
          }
        } else if (parsedMessage.command === 'forceRun') {
          if (parsedMessage.data && parsedMessage.data.type === 'createdocx') {
            setForceRunResult({
              total: parsedMessage.data.total,
              success: parsedMessage.data.success,
              skipped: parsedMessage.data.skipped,
              fail: parsedMessage.data.fail,
              files_failed: parsedMessage.data.files_failed,
              failure_details: parsedMessage.data.failure_details || []
            });
            setForceRunLoading(false);
            setPopUpOpen(true);
          }
        } else if (parsedMessage.command === 'saveActionSettings') {
          if (parsedMessage.data && parsedMessage.data.id && parsedMessage.data.type === 'createdocx') {
            const savedId = parsedMessage.data.id;
            const savedCustomData = parsedMessage.data.customData;
            setId(savedId); // Update ID to the one from backend
            initialSettings.current = {
              id: savedId,
              enable: isEnabled,
              target: targetPath,
              contentType,
              useCustomContent: contentType === 'custom',
              useGenContent: contentType === 'generated',
              customContent: customContent,
              genContent: genContent,
              useConditions: true,
              conditions: [...conditions],
              data: savedCustomData || initialSettings.current.data || {}
            };
            
            if (onSaveStatusChange) {
              onSaveStatusChange(savedId, false, typeof id === 'string' && id.startsWith('temp_') ? id : null);
            }
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, targetPath, customContent, genContent, isEnabled, contentType, useCustomContent, useGenContent, conditions, onSaveStatusChange, id]);

  const updateSaveDisabledState = useCallback(() => {
    if (initialSettings.current && initialSettings.current.id !== undefined) {
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);
      const currentConditionsJSON = JSON.stringify(conditions);

      // Check each condition individually
      const cond1 = targetPath === (initialSettings.current.target || '');
      const cond2 = isEnabled === (initialSettings.current.enable || false);
      const cond3 = contentType === (initialSettings.current.contentType || 'custom');
      const cond4 = useCustomContent === (initialSettings.current.useCustomContent !== undefined ? initialSettings.current.useCustomContent : true);
      const cond5 = useGenContent === (initialSettings.current.useGenContent !== undefined ? initialSettings.current.useGenContent : false);
      const cond6 = useCustomContent ? customContent === (initialSettings.current.customContent || '') : true;
      const cond7 = useGenContent ? genContent === (initialSettings.current.genContent || '') : true;
      const cond8 = currentConditionsJSON === initialConditionsJSON;

      const unchanged = cond1 && cond2 && cond3 && cond4 && cond5 && cond6 && cond7 && cond8;
      const hasChanges = !unchanged;
      
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasChanges);
      }

      if (/[\\/]$/.test(targetPath)) {
        setIsSaveDisabled(true);
      } else {
        setIsSaveDisabled(unchanged);
      }
    }
  }, [targetPath, customContent, genContent, isEnabled, contentType, useCustomContent, useGenContent, conditions, id, onSaveStatusChange]);

  useEffect(() => {
    updateSaveDisabledState();
  }, [targetPath, customContent, genContent, isEnabled, contentType, useCustomContent, useGenContent, conditions, updateSaveDisabledState]);

  const handleSave = () => {
    const data = {
      command: 'saveActionSettings',
      id: id,
      caseid: caseid,
      type: 'createdocx',
      enable: isEnabled ? '1' : '0',
      interval: '0',
      data: {
        target: targetPath,
        contentType,
        useCustomContent,
        customContent: customContent,
        useConditions: true,
        conditions,
        useGenContent,
        genContent: genContent
      }
    };
    if (ws) {
      ws.send(JSON.stringify(data));
    }
  };

  const handleForceRun = (mode = 'all_events', selectedTimeeventId = null, persistent = false) => {
    if (!ws) {
      return;
    }

    try {
      setForceRunLoading(true);
      const data = buildForceRunPayload({
        caseid,
        exportId: id,
        selection: mode,
        timeeventId: selectedTimeeventId,
        persistent,
      });
      ws.send(JSON.stringify(data));
    } catch (error) {
      console.error('Failed to build force run payload:', error);
      setForceRunLoading(false);
    }
  };

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };
    
  const handleTagSelect = (value) => {
    if (menuType === 'main') {
      targetPathRef.current?.insertTag(value);
    } else if (menuType === 'content') {
      customContentRef.current?.insertTag(value);
    } else if (menuType === 'gen') {
      genContentRef.current?.insertTag(value);
    }
    handleMainTagMenuClose();
  };

  const handleConditionsChange = useCallback((newConditions) => {
    setConditions(newConditions);
  }, []);

  // Function to get save button tooltip message
  const getSaveTooltipMessage = () => {
    if (!isSaveDisabled) {
      return t('create_docx.save_changes');
    }

    // Check if path ends with separator
    if (/[\\/]$/.test(targetPath)) {
      return t('create_docx.complete_file_path');
    }

    // Check if target path is empty
    if (!targetPath.trim()) {
      return t('create_docx.enter_target_path');
    }

    // If we have initial settings, check what's different
    if (initialSettings.current && Object.keys(initialSettings.current).length > 0) {
      const currentConditionsJSON = JSON.stringify(conditions);
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);

      // Check for specific missing content based on content type
      if (contentType === 'custom' && !customContent.trim()) {
        return t('create_docx.enter_custom_content');
      } else if (contentType === 'generated' && !genContent.trim()) {
        return t('create_docx.enter_generation_prompt');
      }

      // Check if there are actual changes
      const hasTargetChange = targetPath !== (initialSettings.current.target || '');
      const hasEnabledChange = isEnabled !== (initialSettings.current.enable || false);
      const hasContentTypeChange = contentType !== (initialSettings.current.contentType || 'custom');
      const hasCustomContentChange = customContent !== (initialSettings.current.customContent || '');
      const hasGenContentChange = genContent !== (initialSettings.current.genContent || '');
      const hasConditionsChange = currentConditionsJSON !== initialConditionsJSON;
      const hasUseCustomChange = useCustomContent !== (initialSettings.current.useCustomContent || true);
      const hasUseGenChange = useGenContent !== (initialSettings.current.useGenContent || false);

      if (hasTargetChange || hasEnabledChange || hasContentTypeChange || hasCustomContentChange || 
          hasGenContentChange || hasConditionsChange || hasUseCustomChange || hasUseGenChange) {
        return t('create_docx.click_to_save');
      }
    } else {
      // No initial settings loaded yet
      return t('create_docx.loading_settings');
    }

    // No changes made
    return t('create_docx.no_changes_to_save');
  };


  const closePopUp = () => {
    setPopUpOpen(false);
    setForceRunResult(null);
  };
  
  return (
    <ActionSettingsLayout
      title={t('create_docx.title')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={closePopUp}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      primaryColor="#5757b0"
      saveTooltip={getSaveTooltipMessage()}
    >
      <Box sx={{ width: '100%', p: 0 }}>
        <Box sx={{ 
          p: 0, 
          backgroundColor: '#fdfdff',
          opacity: isEnabled ? 1 : 0.8, 
        }}>


            {/* Where to save */}
            <Card sx={{
              mb: 2,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #5757b010',
              overflow: 'hidden',
            }}>
              <CardHeader
                title={
                  <Typography sx={{
                    fontWeight: 700,
                    color: '#444',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Box component="span" sx={{
                      bgcolor: '#5757b0',
                      color: 'white',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.7,
                      mr: { xs: 1, sm: 2 },
                      fontSize: '0.85rem',
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                    }}>
                      {t('auto_create_file.where_label')}
                    </Box>
                    {t('create_docx.target_document_path')}
                  </Typography>
                }
                sx={{
                  borderBottom: '1px solid #f0f0f8',
                  backgroundColor: '#fafbff',
                  p: { xs: 1.5, sm: 2.5 },
                }}
              />
              <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
                <TagEditor
                    caseid={caseid}
                  ref={targetPathRef}
                  value={targetPath}
                  onChange={setTargetPath}
                  placeholder={t('create_docx.target_path_placeholder')}
                  minHeight="40px"
                  singleLine
                  onTagClick={(e) => handleMenuOpen(e, 'main')}
                  tags={tags}
                />

                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                  mb: 1,
                }}>
                  <Button
                    size="medium"
                    variant="outlined"
                    onClick={handleBrowseClick}
                    disabled={browsingDisabled}
                    startIcon={<BrowseIcon />}
                    sx={{
                      color: '#5757b0',
                      borderColor: '#5757b030',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#f0f0ff',
                        borderColor: '#5757b0',
                        boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                      },
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                    }}
                  >
                    {t('auto_create_file.browse')}
                  </Button>

                  {/* Insert Main Tags */}
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                  }}>
                    {customDataTags.slice(0, 2).map((tag) => (
                      <Chip
                        key={tag.value}
                        label={tag.label}
                        onClick={() => {
                          if (targetPathRef.current) {
                            targetPathRef.current.focus();
                            setTimeout(() => {
                              targetPathRef.current.insertTag(tag.value);
                            }, 10);
                          }
                        }}
                        size="small"
                        sx={{
                          backgroundColor: '#f0f0ff',
                          color: '#5757b0',
                          borderColor: '#5757b030',
                          border: '1px solid',
                          '&:hover': {
                            backgroundColor: '#e0e0ff',
                            boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                          },
                          fontWeight: 600,
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Content Options */}
            <Card sx={{
              mb: 2,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #5757b010',
              overflow: 'hidden',
            }}>
              <CardHeader
                title={
                  <Typography sx={{
                    fontWeight: 700,
                    color: '#444',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Box component="span" sx={{
                      bgcolor: '#5757b0',
                      color: 'white',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.7,
                      mr: { xs: 1, sm: 2 },
                      fontSize: '0.85rem',
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                    }}>
                      {t('auto_create_file.content_label')}
                    </Box>
                    {t('create_docx.document_content')}
                  </Typography>
                }
                sx={{
                  borderBottom: '1px solid #f0f0f8',
                  backgroundColor: '#fafbff',
                  p: { xs: 1.5, sm: 2.5 },
                }}
              />
              <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend" sx={{
                    fontWeight: 600,
                    color: '#444',
                    fontSize: '0.95rem',
                    mb: 1,
                  }}>
                    {t('create_docx.content_source')}
                  </FormLabel>
                  <RadioGroup
                    value={contentType}
                    onChange={(e) => {
                      const newContentType = e.target.value;
                      setContentType(newContentType);
                      setUseCustomContent(newContentType === 'custom');
                      setUseGenContent(newContentType === 'generated');
                    }}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel 
                      value="custom" 
                      control={
                        <Radio sx={{
                          color: '#5757b0',
                          '&.Mui-checked': {
                            color: '#5757b0',
                          },
                        }} />
                      } 
                      label={
                        <Typography sx={{
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          color: '#444',
                        }}>
                          {t('create_docx.custom_content_option')}
                        </Typography>
                      }
                      sx={{ mb: 1 }}
                    />
                    <FormControlLabel 
                      value="generated" 
                      control={
                        <Radio sx={{
                          color: '#5757b0',
                          '&.Mui-checked': {
                            color: '#5757b0',
                          },
                        }} />
                      } 
                      label={
                        <Typography sx={{
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          color: '#444',
                        }}>
                          {t('create_docx.ai_generated_option')}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {/* Custom Content Section */}
                {contentType === 'custom' && (
                  <Box sx={{ ml: 0, mt: 1.5 }}>
                    <TagEditor
                    caseid={caseid}
                      ref={customContentRef}
                      value={customContent}
                      onChange={setCustomContent}
                      placeholder={t('create_docx.custom_content_placeholder')}
                      minHeight="100px"
                      onTagClick={(e) => handleMenuOpen(e, 'content')}
                      tags={tags}
                    />
                  </Box>
                )}

                {/* Generative Content Section */}
                {contentType === 'generated' && (
                  <Box sx={{ ml: 0, mt: 1.5 }}>
                    <TagEditor
                    caseid={caseid}
                      ref={genContentRef}
                      value={genContent}
                      onChange={setGenContent}
                      placeholder={t('create_docx.ai_prompt_placeholder')}
                      minHeight="100px"
                      tags={tags}
                      onTagClick={(e) => handleMenuOpen(e, 'gen')}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons Removed - Handled by Layout */}
        </Box>
      </Box>

      {/* Main/Content Tag dropdown */}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose}
        menuType={menuType}
        additionalTags={customDataTags}
      />

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCreateDocx);

